<template>
  <div class="leagues-list">
    <!-- Section: Sleduji -->
    <div class="ios-leagues-section" v-if="userStore.isAuthenticated">
      <div class="ios-section-header">
      <span class="ios-section-title">{{ t('iWatch') }}</span>
      </div>
      <div class="section-content" :class="{ 'section-content--menu-active': menuOpenCount > 0 }">
        <TransitionGroup name="league-list" tag="div">
          <SectionRow
            v-for="(league, index) in watchedLeagues" 
            :key="'league-' + (league._id || league.urlTitle)"
            :can-edit="true"
            :is-last="index === watchedLeagues.length - 1"
            @click="navigateToLeague(league)"
            @menu-open="menuOpenCount++"
            @menu-close="menuOpenCount--"
          >
            <div class="ios-row-icon">
              <img v-if="league.imageUrl" :src="`${UPLOADS}/leagues/h75-${league.imageUrl}`" :alt="league.title" />
              <v-icon v-else size="24" color="grey">mdi-star-outline</v-icon>
            </div>
            <div class="ios-row-info">
              <div class="ios-row-name">
                {{ league.title }}
              </div>
              <div class="org-type-chip">
                <span class="league-type-badge">{{ (league as any).type === 'academy' ? t('typeAcademy') : t('typeCompetition') }}</span>
              </div>
              <div class="compact-date" v-if="getLeagueSubtitle(league)">
                {{ getLeagueSubtitle(league) }}
              </div>
              <div class="role-chips" v-if="getRoleChips(league).length">
                <span 
                  v-for="chip in getRoleChips(league)" 
                  :key="chip.key" 
                  class="role-chip"
                >{{ chip.label }}</span>
              </div>
            </div>

            <template #menu="{ close }">
              <template v-if="canEditArticle(league)">
                <div class="context-menu-item" @click="openEditPage(league); close()">
                  <span>{{ t('nav.organizationSettings') }}</span>
                  <v-icon size="18">mdi-cog-outline</v-icon>
                </div>
                <div class="context-menu-divider"></div>
              </template>
              <div class="context-menu-item" @click="toggleWatch(league); close()">
                <span>{{ t('stopWatching') }}</span>
                <v-icon size="18">mdi-eye-off-outline</v-icon>
              </div>
            </template>
          </SectionRow>
        </TransitionGroup>
        
        <!-- Empty state -->
        <div v-if="watchedLeagues.length === 0" class="empty-watching">
          {{ userStore.isAuthenticated ? t('noWatchedOrganizations') : t('loginToWatch') }}
        </div>
      </div>
    </div>

    <!-- Divider 
    <div class="section-divider" v-if="userStore.isAuthenticated && allOrganizations.length > 0"></div>
-->
    <!-- Section: Organizace -->
    <div v-if="!userStore.isAuthenticated || !!props.searchQuery" class="ios-leagues-section">
       <div class="ios-section-header">
      <span class="ios-section-title">{{ userStore.isAuthenticated ? t('otherOrganizations') : t('organizations') }}</span>
      </div>
      <div class="section-content" :class="{ 'section-content--menu-active': menuOpenCount > 0 }">
        <TransitionGroup name="league-list" tag="div">
          <SectionRow
            v-for="(league, index) in visibleOrganizations" 
            :key="'org-' + (league._id || league.urlTitle)"
            :can-edit="userStore.isAuthenticated && league.isPublic !== false"
            :show-lock="league.isPublic === false && userStore.isAuthenticated"
            :is-last="!hasMoreOrganizations && index === visibleOrganizations.length - 1"
            @click="navigateToLeague(league)"
            @menu-open="menuOpenCount++"
            @menu-close="menuOpenCount--"
          >
            <div class="ios-row-icon">
              <img v-if="league.imageUrl" :src="`${UPLOADS}/leagues/h75-${league.imageUrl}`" :alt="league.title" />
              <v-icon v-else size="24" color="grey">{{ (league as any).type === 'academy' ? 'mdi-school-outline' : 'mdi-trophy-outline' }}</v-icon>
            </div>
            <div class="ios-row-info">
              <div class="ios-row-name">
                {{ league.title }}
                <span v-if="isWatching(league)" class="watching-badge">{{ t('iWatch') }}</span>
              </div>
              <div class="org-type-chip">
                <span class="league-type-badge">{{ (league as any).type === 'academy' ? t('typeAcademy') : t('typeCompetition') }}</span>
              </div>
              <div class="compact-date" v-if="getLeagueSubtitle(league)">
                {{ getLeagueSubtitle(league) }}
              </div>
              <div class="role-chips" v-if="getRoleChips(league).length">
                <span 
                  v-for="chip in getRoleChips(league)" 
                  :key="chip.key" 
                  class="role-chip"
                >{{ chip.label }}</span>
              </div>
            </div>

            <template #menu="{ close }">
              <template v-if="canEditArticle(league)">
                <div class="context-menu-item" @click="openEditPage(league); close()">
                  <span>{{ t('nav.organizationSettings') }}</span>
                  <v-icon size="18">mdi-cog-outline</v-icon>
                </div>
                <div class="context-menu-divider"></div>
              </template>
              <div class="context-menu-item" @click="toggleWatch(league); close()">
                <span>{{ isWatching(league) ? t('stopWatching') : t('startWatching') }}</span>
                <v-icon size="18">{{ isWatching(league) ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
              </div>
            </template>
          </SectionRow>
        </TransitionGroup>

        <!-- Empty state -->
        <div v-if="allOrganizations.length === 0" class="empty-watching">
          {{ t('noOrganizations') }}
        </div>

      </div>

      <!-- Load more -->
      <div v-if="hasMoreOrganizations" style="padding: 0 16px; margin-top: 8px;">
        <button class="load-more-btn" @click="showMoreOrganizations">
          {{ t('loadMore') }}
        </button>
      </div>
    </div>
  </div>

  <!-- No Season Modal -->
  <CenteredModal v-model="showNoSeasonModal" :show-close-button="true" max-width="350px">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="warning">mdi-calendar-remove</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ t('noSeasonTitle') }}</h3>
      <p class="delete-confirm-subtitle">
        <template v-if="noSeasonLeagueIsAdmin">
          {{ t('noSeasonAdmin', { edit: t('nav.edit') }) }}
        </template>
        <template v-else>
          {{ t('noSeasonUser') }}
        </template>
      </p>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="showNoSeasonModal = false">
          {{ t('close') }}
        </button>
        <button 
          v-if="noSeasonLeagueIsAdmin" 
          class="delete-confirm-btn delete-confirm-btn--edit" 
          @click="goToEditNoSeasonLeague"
        >
          {{ t('nav.edit') }}
        </button>
      </div>
    </div>
  </CenteredModal>

</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { ILeague } from '@/types'
import { useUserStore } from '@/stores/user/userStore'
import { useRouter } from 'vue-router'
import axiosInstance from '@/services/axiosInstance'
import { useI18n } from 'vue-i18n'
import SectionRow from '@/components/SectionRow.vue'

const { t } = useI18n()
const UPLOADS = import.meta.env.VITE_UPLOADS

const router = useRouter()
const userStore = useUserStore()

const props = defineProps<{
  organizations: ILeague[]
  searchQuery?: string
}>()

const emit = defineEmits<{
  (e: 'watch-league', league: ILeague): void
  (e: 'unwatch-league', league: ILeague): void
  (e: 'edit-league', league: ILeague): void
}>()

type LeagueAdmin = string | { _id: string }

// Menu open tracking for overflow control
const menuOpenCount = ref(0)

// Show limits for sections
const ITEMS_PER_PAGE = 5

// All organizations — for authenticated users, exclude watched ones (unless searching)
const allOrganizations = computed(() => {
  if (userStore.isAuthenticated && !props.searchQuery) {
    return props.organizations.filter(l => !watchedLeagueIds.value.includes(leagueId(l)))
  }
  return props.organizations
})
const organizationsLimit = ref(ITEMS_PER_PAGE)
const visibleOrganizations = computed(() => allOrganizations.value.slice(0, organizationsLimit.value))
const hasMoreOrganizations = computed(() => allOrganizations.value.length > organizationsLimit.value)
const showMoreOrganizations = () => { organizationsLimit.value += ITEMS_PER_PAGE }

// Watched leagues (from both leagues + academies)
const watchedLeagues = computed(() => {
  return props.organizations.filter(l => watchedLeagueIds.value.includes(leagueId(l)))
})

/* 🔐 práva: globální admin NEBO admin konkrétní league */
const isGlobalAdmin = computed(() => (userStore.user?.userRoles || []).includes('administrator'))

function canEditArticle(article: ILeague): boolean {
  if (isGlobalAdmin.value) return true
  const me = userStore.user?._id
  if (!me) return false
  const admins = (article.admins || []) as LeagueAdmin[]
  return admins.some((a) => {
    if (a && typeof a === 'object') return a._id === me
    return a === me
  })
}

// Watched leagues
const watchedLeagueIds = computed<string[]>(() =>
  (userStore.user?.watchedLeagues ?? []).map((x: any) =>
    typeof x === 'string' ? x : x?.toString?.() ?? String(x)
  )
)

function leagueId(league: ILeague): string {
  const anyLeague = league as any
  return anyLeague?._id?.toString?.() ?? String(anyLeague._id ?? anyLeague.id ?? league.urlTitle)
}

function isWatching(league: ILeague): boolean {
  return watchedLeagueIds.value.includes(leagueId(league))
}

// Role chips
const roleKeys: Record<string, string> = {
  admins: 'roleAdmin',
  timekeepers: 'roleTimekeeper',
}

const getRoleChips = (league: any) => {
  const matched: string[] = league.matchedRoles || []
  const chips: { key: string; label: string }[] = []
  const seen = new Set<string>()

  for (const role of matched) {
    const key = roleKeys[role]
    if (key) {
      const label = t(key)
      if (!seen.has(label)) {
        seen.add(label)
        chips.push({ key: role, label })
      }
    }
  }
  return chips
}

// Subtitle helper
const getLeagueLocation = (league: ILeague): string | null => {
  const loc = (league as any).location
  if (!loc) return null
  if (typeof loc === 'string') return loc || null
  if (typeof loc === 'object' && loc.city) return loc.city
  if (typeof loc === 'object' && loc.label) return loc.label
  return null
}

const getLeagueSubtitle = (league: ILeague): string | null => {
  const parts: string[] = []
  const city = getLeagueLocation(league)
  if (city) parts.push(city)
  const wc = (league as any).watchingCounter || 0
  if (wc > 0) parts.push(t('nWatchers', { count: wc }))
  return parts.join(' · ') || null
}

// No season modal
const showNoSeasonModal = ref(false)
const noSeasonLeague = ref<ILeague | null>(null)
const noSeasonLeagueIsAdmin = computed(() => {
  if (!noSeasonLeague.value) return false
  return canEditArticle(noSeasonLeague.value)
})

function goToEditNoSeasonLeague() {
  showNoSeasonModal.value = false
  if (noSeasonLeague.value) {
    emit('edit-league', noSeasonLeague.value)
  }
}

// Navigation
function navigateToLeague(league: ILeague) {
  const id = league.urlTitle || league._id
  const type = String((league as any).type ?? '').toLowerCase()
  
  if (type === 'academy') {
    router.push(`/leagues/${id}/academy/schedule`)
    return
  }
  
  const season = (league as any).activeSeason?.year 
    ?? (league as any).activeSeason?.urlTitle 
    ?? null
  
  if (season) {
    router.push(`/leagues/${id}/${season}`)
  } else {
    noSeasonLeague.value = league
    showNoSeasonModal.value = true
  }
}

// Actions
const openEditPage = (league: ILeague) => {
  emit('edit-league', league)
}

const toggleWatch = (league: ILeague) => {
  if (isWatching(league)) {
    emit('unwatch-league', league)
  } else {
    emit('watch-league', league)
  }
}


</script>

<style lang="scss" scoped>
.leagues-list {
  padding: 0;
}

.ios-leagues-section {
  margin-bottom: 24px;
}

.section-divider {
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(var(--v-theme-sheetText), 0.12) 15%,
    rgba(var(--v-theme-sheetText), 0.12) 85%,
    transparent 100%
  );
  margin: 0 16px 24px;
}

.section-title {
  display: block;
  font-size: 1.17em;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetTextSecondary));
  padding: 0 16px;
  margin: 0 0 8px 0;
}

.section-row-wrapper {
  position: relative;
}

.league-type-badge {
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 500;
  color: rgba(var(--v-theme-sheetText), 0.45);
  background: rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 4px;
  padding: 1px 5px;
  vertical-align: middle;
  letter-spacing: 0.02em;
  line-height: 1.4;
}

.org-type-chip {
  margin-top: 2px;
  line-height: 1;
}

.role-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.role-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(var(--v-theme-ice), 0.1);
  color: rgb(var(--v-theme-ice));
  border: 1px solid rgba(var(--v-theme-ice), 0.2);
  line-height: 18px;
}

.watching-badge {
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 500;
  color: rgb(var(--v-theme-brand));
  background: rgba(var(--v-theme-brand), 0.12);
  border-radius: 4px;
  padding: 1px 5px;
  margin-left: 6px;
  vertical-align: middle;
  letter-spacing: 0.02em;
  line-height: 1.4;
}

.empty-watching {
  padding: 20px 16px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.45);
  font-size: 15px;
}

/* TransitionGroup animations */
.league-list-move,
.league-list-enter-active,
.league-list-leave-active {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.league-list-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.league-list-leave-to {
  opacity: 0;
  transform: translateX(30px) scale(0.95);
}

.league-list-leave-active {
  position: absolute;
  width: 100%;
}

// Delete confirm dialog
.delete-confirm-dialog {
  padding: 24px;
  text-align: center;
}

.delete-confirm-icon {
  margin-bottom: 16px;
}

.delete-confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.delete-confirm-subtitle {
  font-size: 0.9rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 24px;
}

.delete-confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.delete-confirm-btn {
  padding: 10px 24px;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &--cancel {
    background: rgba(var(--v-theme-on-surface), 0.1);
    color: inherit;
  }

  &--confirm {
    background: rgb(var(--v-theme-error));
    color: white;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &--edit {
    background: rgb(var(--v-theme-primary));
    color: white;
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }
}
</style>
